import { useMemo, useCallback } from "react";
import { useACLContext } from "../context";
import { usePermissionHelpers } from "./usePermissionHelpers";
import { useCanMultiple } from "./useCanMultiple";
import type { ResourceType, PermissionAction } from "@shield-acl/core";

/**
 * Interface completa de ACL para um recurso específico
 */
export interface ResourceACL<TContext = any> {
  /** Nome do recurso */
  resource: ResourceType;

  /** Verificações CRUD básicas */
  canRead: (context?: TContext) => boolean;
  canCreate: (context?: TContext) => boolean;
  canUpdate: (context?: TContext) => boolean;
  canDelete: (context?: TContext) => boolean;
  canManage: (context?: TContext) => boolean;

  /** Aliases comuns */
  canList: (context?: TContext) => boolean;
  canView: (context?: TContext) => boolean;
  canEdit: (context?: TContext) => boolean;
  canModify: (context?: TContext) => boolean;
  canAccess: (context?: TContext) => boolean;

  /** Verificação customizada de ação */
  can: (action: PermissionAction, context?: TContext) => boolean;
  cannot: (action: PermissionAction, context?: TContext) => boolean;

  /** Status geral de permissões CRUD */
  permissions: {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    manage: boolean;
  };

  /** Helpers de verificação múltipla */
  hasAnyPermission: boolean;
  hasAllCrudPermissions: boolean;
  hasReadOnlyAccess: boolean;
  hasWriteAccess: boolean;
  hasFullAccess: boolean;

  /** Lista de ações disponíveis para este recurso */
  availableActions: string[];
}

/**
 * Hook contextual que fornece interface completa de ACL para um recurso específico
 * Automaticamente deduz e organiza todas as permissões relacionadas ao recurso
 *
 * @param resource - O recurso para contextualizar as permissões
 * @returns Objeto com interface completa de ACL para o recurso
 *
 * @example
 * ```tsx
 * // Em um componente de gerenciamento de posts
 * const postsACL = useResourceACL("posts")
 *
 * // Verificações CRUD básicas
 * if (!postsACL.canRead()) {
 *   return <AccessDenied />
 * }
 *
 * return (
 *   <div>
 *     <PostList />
 *
 *     {postsACL.canCreate() && (
 *       <CreatePostButton />
 *     )}
 *
 *     {postsACL.hasWriteAccess && (
 *       <BulkActions />
 *     )}
 *
 *     {postsACL.hasFullAccess && (
 *       <AdminPanel />
 *     )}
 *   </div>
 * )
 *
 * // Em um componente de item específico
 * function PostItem({ post }) {
 *   const postsACL = useResourceACL("posts")
 *
 *   // Verificação com contexto
 *   const canEditThis = postsACL.canEdit({ postId: post.id })
 *   const canDeleteThis = postsACL.canDelete({ postId: post.id })
 *
 *   return (
 *     <div>
 *       <h3>{post.title}</h3>
 *       {canEditThis && <EditButton />}
 *       {canDeleteThis && <DeleteButton />}
 *     </div>
 *   )
 * }
 * ```
 */
export function useResourceACL<TContext = any>(
  resource: ResourceType,
): ResourceACL<TContext> {
  const { can: canCheck, user } = useACLContext<TContext>();
  const helpers = usePermissionHelpers<TContext>();

  // Cria funções de verificação vinculadas ao recurso
  const canRead = useCallback(
    (context?: TContext) => helpers.canRead(resource, context),
    [helpers, resource],
  );

  const canCreate = useCallback(
    (context?: TContext) => helpers.canCreate(resource, context),
    [helpers, resource],
  );

  const canUpdate = useCallback(
    (context?: TContext) => helpers.canUpdate(resource, context),
    [helpers, resource],
  );

  const canDelete = useCallback(
    (context?: TContext) => helpers.canDelete(resource, context),
    [helpers, resource],
  );

  const canManage = useCallback(
    (context?: TContext) => helpers.canManage(resource, context),
    [helpers, resource],
  );

  const canList = useCallback(
    (context?: TContext) => helpers.canList(resource, context),
    [helpers, resource],
  );

  const canView = useCallback(
    (context?: TContext) => helpers.canView(resource, context),
    [helpers, resource],
  );

  const canEdit = useCallback(
    (context?: TContext) => helpers.canEdit(resource, context),
    [helpers, resource],
  );

  const canModify = useCallback(
    (context?: TContext) => helpers.canModify(resource, context),
    [helpers, resource],
  );

  const canAccess = useCallback(
    (context?: TContext) => helpers.canAccess(resource, context),
    [helpers, resource],
  );

  // Verificação customizada de ação
  const can = useCallback(
    (action: PermissionAction, context?: TContext) =>
      canCheck(action, resource, context),
    [canCheck, resource],
  );

  const cannot = useCallback(
    (action: PermissionAction, context?: TContext) =>
      !canCheck(action, resource, context),
    [canCheck, resource],
  );

  // Verifica permissões CRUD básicas (sem contexto)
  const crudPermissions = useCanMultiple<TContext>(
    [
      { action: `${resource}.read`, resource },
      { action: `${resource}.create`, resource },
      { action: `${resource}.update`, resource },
      { action: `${resource}.delete`, resource },
      { action: `${resource}.manage`, resource },
    ],
    ["read", "create", "update", "delete", "manage"],
  );

  // Calcula helpers de status
  const hasAnyPermission = crudPermissions.anyAllowed;
  const hasAllCrudPermissions =
    crudPermissions.results.read &&
    crudPermissions.results.create &&
    crudPermissions.results.update &&
    crudPermissions.results.delete;
  const hasReadOnlyAccess =
    crudPermissions.results.read &&
    !crudPermissions.results.create &&
    !crudPermissions.results.update &&
    !crudPermissions.results.delete;
  const hasWriteAccess =
    crudPermissions.results.create ||
    crudPermissions.results.update ||
    crudPermissions.results.delete;
  const hasFullAccess = crudPermissions.results.manage || hasAllCrudPermissions;

  // Lista ações disponíveis para este recurso
  const availableActions = useMemo(() => {
    const actions: string[] = [];

    // Ações CRUD padrão
    const standardActions = [
      "read",
      "list",
      "view",
      "create",
      "add",
      "new",
      "update",
      "edit",
      "modify",
      "delete",
      "remove",
      "manage",
      "*",
    ];

    // Verifica cada ação padrão
    standardActions.forEach((action) => {
      if (
        canCheck(`${resource}.${action}`, resource) ||
        canCheck(action, resource)
      ) {
        actions.push(`${resource}.${action}`);
      }
    });

    // Remove duplicatas e ordena
    return [...new Set(actions)].sort();
  }, [canCheck, resource, user]);

  // Retorna objeto completo
  return useMemo(
    () => ({
      resource,

      // Funções CRUD
      canRead,
      canCreate,
      canUpdate,
      canDelete,
      canManage,

      // Aliases
      canList,
      canView,
      canEdit,
      canModify,
      canAccess,

      // Verificação customizada
      can,
      cannot,

      // Status de permissões
      permissions: {
        read: crudPermissions.results.read,
        create: crudPermissions.results.create,
        update: crudPermissions.results.update,
        delete: crudPermissions.results.delete,
        manage: crudPermissions.results.manage,
      },

      // Helpers de status
      hasAnyPermission,
      hasAllCrudPermissions,
      hasReadOnlyAccess,
      hasWriteAccess,
      hasFullAccess,

      // Ações disponíveis
      availableActions,
    }),
    [
      resource,
      canRead,
      canCreate,
      canUpdate,
      canDelete,
      canManage,
      canList,
      canView,
      canEdit,
      canModify,
      canAccess,
      can,
      cannot,
      crudPermissions,
      hasAnyPermission,
      hasAllCrudPermissions,
      hasReadOnlyAccess,
      hasWriteAccess,
      hasFullAccess,
      availableActions,
    ],
  );
}

/**
 * Hook que retorna um mapa de ACL para múltiplos recursos
 * Útil quando você precisa verificar permissões de vários recursos de uma vez
 *
 * @param resources - Array de recursos
 * @returns Mapa de recursos para suas interfaces ACL
 *
 * @example
 * ```tsx
 * const acls = useMultipleResourceACL(["posts", "users", "settings"])
 *
 * if (acls.posts.canCreate() && acls.users.canRead()) {
 *   // Pode criar posts e ler usuários
 * }
 * ```
 */
export function useMultipleResourceACL<TContext = any>(
  resources: ResourceType[],
): Record<ResourceType, ResourceACL<TContext>> {
  const acls = resources.map((resource) => ({
    resource,
    acl: useResourceACL<TContext>(resource),
  }));

  return useMemo(() => {
    const map: Record<ResourceType, ResourceACL<TContext>> = {};
    acls.forEach(({ resource, acl }) => {
      map[resource] = acl;
    });
    return map;
  }, [acls]);
}
